import React, { createContext, useContext, useEffect, useState } from 'react';
import { rolePermissionsApi } from '../services/api-services';

interface Permissions {
  menus: string[];
  actions: string[];
}

interface PermissionContextType {
  permissions: Permissions;
  loading: boolean;
  hasMenu: (key: string) => boolean;
  hasAction: (action: string) => boolean;
  reload: () => void;
}

const defaultPermissions: Permissions = { menus: [], actions: [] };

const PermissionContext = createContext<PermissionContextType>({
  permissions: defaultPermissions,
  loading: true,
  hasMenu: () => true,
  hasAction: () => true,
  reload: () => {},
});

export const usePermission = () => useContext(PermissionContext);

export const PermissionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [permissions, setPermissions] = useState<Permissions>(defaultPermissions);
  const [loading, setLoading] = useState(true);
  const loadedRef = React.useRef(false);

  const loadPermissions = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const res: any = await rolePermissionsApi.my();
      setPermissions(res.permissions || defaultPermissions);
    } catch {
      // fallback: allow all if API fails
      setPermissions({ menus: ['*'], actions: ['*'] });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    loadPermissions();
  }, []);

  const hasMenu = (key: string) => {
    if (permissions.menus.includes('*')) return true;
    return permissions.menus.includes(key);
  };

  const hasAction = (action: string) => {
    if (permissions.actions.includes('*')) return true;
    return permissions.actions.includes(action);
  };

  return (
    <PermissionContext.Provider value={{ permissions, loading, hasMenu, hasAction, reload: loadPermissions }}>
      {children}
    </PermissionContext.Provider>
  );
};
